import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, TextField, Button,
  IconButton, Autocomplete, Divider
} from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import SendIcon from '@mui/icons-material/Send';
import AssignmentIcon from '@mui/icons-material/Assignment';
import api from '../api/axiosConfig';

function FieldNoteFeed() {
  const [fieldOptions, setFieldOptions] = useState([]);
  const [selectedField, setSelectedField] = useState(null);
  const [notes, setNotes] = useState([]);
  const [inputText, setInputText] = useState('');

  const [editingNoteId, setEditingNoteId] = useState(null);
  const [editNoteText, setEditNoteText] = useState('');

  useEffect(() => {
    api.get('/api/fieldList')
      .then(res => {
        setFieldOptions(res.data || []);
      })
      .catch(err => console.error("현장 목록 로드 실패:", err));
  }, []);

  const fetchNotes = useCallback(() => {
    if (!selectedField) {
      setNotes([]);
      return;
    }

    api.get(`/api/fieldNote/?fieldId=${selectedField.FIELD_ID}`)
      .then(res => {
        setNotes(res.data || []);
      })
      .catch(err => console.error("메모 로드 실패:", err));
  }, [selectedField]);

  useEffect(() => {
    fetchNotes();
  }, [fetchNotes]);

  const handlePostNote = () => {
    if (!selectedField) {
      alert('현장을 먼저 선택해주세요.');
      return;
    }
    if (!inputText.trim()) {
      alert('메모 내용을 입력해주세요.');
      return;
    }

    const newNote = {
      FIELD_ID: selectedField.FIELD_ID,
      NOTE: inputText
    };

    api.post(`/api/fieldNote/save`, { createdList: [newNote], updatedList: [] })
      .then(() => {
        setInputText('');
        fetchNotes();
      })
      .catch(err => alert('작성 중 오류가 발생했습니다.'));
  };

  const handleDelete = (noteId) => {
    if (!window.confirm('이 메모를 삭제하시겠습니까?')) return;

    api.post(`/api/fieldNote/delete`, [{ NOTE_ID: noteId }])
      .then(() => {
        fetchNotes();
      })
      .catch(err => alert('삭제 중 오류가 발생했습니다.'));
  };

  const handleEditStart = (note) => {
    setEditingNoteId(note.NOTE_ID);
    setEditNoteText(note.NOTE);
  };

  const handleEditCancel = () => {
    setEditingNoteId(null);
    setEditNoteText('');
  };

  const handleEditSave = (noteId) => {
    if (!editNoteText.trim()) {
      alert('수정할 내용을 입력해주세요.');
      return;
    }

    const updatedNote = {
      NOTE_ID: noteId,
      NOTE: editNoteText
    };

    api.post(`/api/fieldNote/save`, { createdList: [], updatedList: [updatedNote] })
      .then(() => {
        setEditingNoteId(null);
        setEditNoteText('');
        fetchNotes();
      })
      .catch(err => alert('수정 중 오류가 발생했습니다.'));
  };

  return (
    <Box sx={{ maxWidth: 1200, margin: '0 auto', p: { xs: 2, md: 4 } }}>

      {/* --- 상단 헤더 & 검색 영역 --- */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 5 }}>
        <Typography variant="h5" fontWeight="800" sx={{ display: 'flex', alignItems: 'center', gap: 1.5, color: '#1f2937' }}>
          <Box sx={{ p: 1, bgcolor: '#eff6ff', borderRadius: 2, display: 'flex' }}>
            <AssignmentIcon sx={{ color: '#3b82f6' }} />
          </Box>
          현장 노트
        </Typography>

        <Autocomplete
          size="small"
          options={fieldOptions}
          getOptionLabel={(option) => option.FIELD_NAME || ''}
          isOptionEqualToValue={(option, value) => option.FIELD_ID === value.FIELD_ID}
          value={selectedField}
          onChange={(event, newValue) => {
            setSelectedField(newValue);
            handleEditCancel();
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              placeholder="현장명 검색..."
              sx={{
                '& .MuiOutlinedInput-root': { borderRadius: '20px', bgcolor: '#f9fafb' }
              }}
            />
          )}
          noOptionsText="검색 결과가 없습니다"
          sx={{ width: 260 }}
        />
      </Box>

      {/* --- 부드러운 스타일의 작성 영역 --- */}
      <Box sx={{
        mb: 6, p: 3,
        bgcolor: '#ffffff',
        borderRadius: 4,
        border: '1px solid #f3f4f6',
        boxShadow: '0 4px 20px rgba(0,0,0,0.03)' // 아주 연한 그림자
      }}>
        <TextField
          fullWidth
          multiline
          minRows={2}
          maxRows={5}
          placeholder={selectedField ? "이 현장의 특이사항이나 변동 내역을 남겨주세요." : "현장을 먼저 검색하여 선택해주세요."}
          value={inputText}
          onChange={(e) => setInputText(e.target.value)}
          disabled={!selectedField}
          variant="outlined"
          sx={{
            mb: 2,
            '& .MuiOutlinedInput-root': {
              bgcolor: '#f9fafb',
              borderRadius: 3,
              '& fieldset': { border: 'none' }, // 기본 테두리 제거
              '&:hover fieldset': { border: '1px solid #e5e7eb' },
              '&.Mui-focused fieldset': { border: '1px solid #3b82f6' },
            }
          }}
        />
        <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
          <Button
            variant="contained"
            endIcon={<SendIcon sx={{ fontSize: 18 }} />}
            onClick={handlePostNote}
            disabled={!selectedField || !inputText.trim()}
            disableElevation
            sx={{
              borderRadius: '20px',
              px: 3,
              py: 0.8,
              textTransform: 'none',
              fontWeight: 'bold',
              bgcolor: '#3b82f6',
              '&:hover': { bgcolor: '#2563eb' }
            }}
          >
            기록하기
          </Button>
        </Box>
      </Box>

      {/* --- 타임라인 리스트 영역 --- */}
      <Box sx={{ display: 'flex', flexDirection: 'column' }}>
        {!selectedField ? (
          <Box sx={{ textAlign: 'center', py: 8, bgcolor: '#f9fafb', borderRadius: 4 }}>
            <Typography color="text.secondary">상단에서 현장을 검색하여 노트를 확인하세요.</Typography>
          </Box>
        ) : notes.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 8, bgcolor: '#f9fafb', borderRadius: 4 }}>
            <Typography color="text.secondary">아직 작성된 노트가 없습니다. 첫 기록을 남겨보세요!</Typography>
          </Box>
        ) : (
          notes.map((note, index) => (
            <Box key={note.NOTE_ID} sx={{ display: 'flex', gap: 2.5, mb: 1 }}>

              {/* 좌측 타임라인 연결선 & 점 */}
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                <Box sx={{
                  width: 14,
                  height: 14,
                  borderRadius: '50%',
                  bgcolor: '#3b82f6',
                  border: '3px solid #eff6ff',
                  mt: 0.5
                }} />
                {/* 마지막 아이템이 아니면 세로선 표시 */}
                {index !== notes.length - 1 && (
                  <Box sx={{ width: 2, flexGrow: 1, bgcolor: '#e5e7eb', my: 0.5, borderRadius: 1 }} />
                )}
              </Box>

              {/* 우측 노트 내용 (말풍선 느낌의 부드러운 Box) */}
              <Box sx={{
                flexGrow: 1,
                bgcolor: '#f8fafc', // 아주 연한 회파랑 배경
                borderRadius: 3,
                p: 2.5,
                mb: 4 // 다음 아이템과의 간격
              }}>
                {/* 헤더 */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
                  <Box>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#374151' }}>
                      {note.FIELD_NAME || '현장'}
                    </Typography>
                    <Typography variant="caption" sx={{ color: '#9ca3af' }}>
                      {note.CREATED_AT || '방금 전'}
                    </Typography>
                  </Box>

                  {/* 제어 버튼 */}
                  <Box sx={{ display: 'flex', gap: 0.5 }}>
                    <IconButton size="small" onClick={() => handleEditStart(note)} sx={{ color: '#6b7280', '&:hover': { bgcolor: '#e5e7eb' } }}>
                      <EditOutlinedIcon fontSize="small" />
                    </IconButton>
                    <IconButton size="small" onClick={() => handleDelete(note.NOTE_ID)} sx={{ color: '#ef4444', '&:hover': { bgcolor: '#fee2e2' } }}>
                      <DeleteOutlineIcon fontSize="small" />
                    </IconButton>
                  </Box>
                </Box>

                {/* 본문 / 수정 모드 */}
                {editingNoteId === note.NOTE_ID ? (
                  <Box>
                    <TextField
                      fullWidth
                      multiline
                      minRows={2}
                      value={editNoteText}
                      onChange={(e) => setEditNoteText(e.target.value)}
                      variant="outlined"
                      sx={{
                        mb: 2,
                        '& .MuiOutlinedInput-root': { bgcolor: '#ffffff', borderRadius: 2 }
                      }}
                      autoFocus
                    />
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                      <Button variant="text" size="small" onClick={handleEditCancel} sx={{ color: '#6b7280', borderRadius: 2 }}>
                        취소
                      </Button>
                      <Button variant="contained" size="small" onClick={() => handleEditSave(note.NOTE_ID)} disableElevation sx={{ borderRadius: 2, bgcolor: '#3b82f6' }}>
                        저장
                      </Button>
                    </Box>
                  </Box>
                ) : (
                  <Typography
                    variant="body1"
                    sx={{
                      whiteSpace: 'pre-wrap',
                      wordBreak: 'break-word',
                      color: '#4b5563',
                      lineHeight: 1.6
                    }}
                  >
                    {note.NOTE}
                  </Typography>
                )}
              </Box>
            </Box>
          ))
        )}
      </Box>

    </Box>
  );
}

export default FieldNoteFeed;